import SimplexNoise from "simplex-noise";
import {
  openAIgenerateImage,
  openAIgenerateImageVariation,
} from "./openAIgenerate";
import { FillMode } from "../hooks/useDrawingOnTerrain";
import {
  drawPixel,
  drawPixelInArray,
  getColorByHeight,
  getColorByHeight2,
  getPixel,
  getPixelFromArray,
} from "./utils";
import { MapProperties } from "./config";
import { GetNoiseFunctionType } from "./generateV2";

export enum MapEnum {
  GREEN_HILLS = "GREEN_HILLS",
  MARS = "MARS",
  ICE_LAND = "ICE_LAND",
  SAFARI = "SAFARI",
  MOUNTAINS = "MOUNTAINS",
}

export type MapType = keyof typeof MapEnum;

export type TextureType = {
  name: TerrainTexture;
  uv?: number;
};

type TerrainTexture = (typeof availableTextures)[number];

export const availableTextures = [
  "sand",
  "desert_sand",
  "swamp",
  "mesa",
  "snowy_mountain",
  "lava",
  "red_mountain",
  "red_sand",
  "red_dirt",
  "sand_rocks",
  "sandstone",
  "rocky_ground",
  "snow",
  "blue_snow",
  "smooth_snow",
  "ice",
  "brown_mud",
  "mud_cracked",
  "rocky_grass",
  "smooth_grass",
  "green_grass",
  "wild_grass",
  "deep_grass",
  "bright_grass",
  "grass1",
  "grass2",
  "mossy_grass",
  "dry_grass",
] as const;

/**
 * @deprecated
 * use generateNoiseV2
 */
export const generateNoiseV1 = (
  noiseCanvas: HTMLCanvasElement | null,
  mapType: MapType
) => {
  if (!noiseCanvas) {
    return;
  }

  const noiseCtx = noiseCanvas.getContext("2d");

  if (!noiseCtx) {
    return;
  }

  const simplex = new SimplexNoise();

  const { minLevel = 0 } = MapProperties[mapType];

  const width = noiseCanvas.width;
  const height = noiseCanvas.height;

  const addBorder = (x: number, y: number, value: number) => {
    if (x < width * 0.1) {
      const ratio = x / (width * 0.1);
      value = 1 / Math.exp(ratio * 4) + value;
    } else if (x > width * 0.9) {
      const dist = width - width * 0.9;
      const ratio = (width - x) / dist;
      value = 1 / Math.exp(ratio * 4) + value;
    }

    if (y < height * 0.1) {
      const ratio = y / (height * 0.1);
      value = 1 / Math.exp(ratio * 4) + value;
    } else if (y > height * 0.9) {
      const dist = height - height * 0.9;
      const ratio = (height - y) / dist;
      value = 1 / Math.exp(ratio * 4) + value;
    }

    return value;
  };

  const noiseSize = 0.005;

  const noiseData = noiseCtx.getImageData(0, 0, width, height);
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      let value = simplex.noise2D(x * noiseSize, y * noiseSize);

      value = addBorder(x, y, value);

      if (value < minLevel) value = minLevel;

      const color = value * 255;

      drawPixel(noiseData, x, y, width, [color, color, color]);
    }
  }

  noiseCtx.putImageData(noiseData, 0, 0);
};

declare global {
  interface FormData {
    getHeaders: () => { [key: string]: string };
  }
}

FormData.prototype.getHeaders = () => {
  return { "Content-Type": "multipart/form-data" };
};

export const generateNoiseWithAI = async (
  mapType: MapType,
  getNoiseContextAndData: GetNoiseFunctionType
) => {
  const contextAndData = getNoiseContextAndData();
  if (!contextAndData) {
    return;
  }

  const { noiseCtx, height, width, noiseCanvas } = contextAndData;

  const prompt = `Height map, black and white, simplex, perlin noise, top view, fbm, domain warping, shape and features of ${mapType}, scale 2x`;
  const file = await new Promise<File>((resolve, reject) => {
    noiseCanvas.toBlob((blob) => {
      if (blob) {
        let file = new File([blob], "fileName.png", { type: "image/png" });
        resolve(file);
      }
      reject();
    }, "image/png");
  });

  console.log("Generating noise for:", prompt);
  const imageBase64 = await openAIgenerateImage(prompt);
  if (imageBase64) {
    return new Promise((resolve) => {
      const image = new Image(width, height);
      image.onload = () => {
        noiseCtx.drawImage(image, 0, 0, image.width, image.height);
        resolve(1);
      };
      image.src = `data:image/png;base64,${imageBase64}`;
    });
  } else {
    console.error("Could not generate noise for prompt: ", prompt);
  }
};

export const generateMixMap = (
  mixMapCanvas: HTMLCanvasElement | null,
  noiseCanvas: HTMLCanvasElement | null,
  levels: [number, number, number]
) => {
  if (!noiseCanvas || !mixMapCanvas) {
    return;
  }

  const noiseCtx = noiseCanvas.getContext("2d");

  const mixMapCtx = mixMapCanvas.getContext("2d");
  if (!noiseCtx || !mixMapCtx) {
    return;
  }

  const width = mixMapCanvas.width;
  const height = mixMapCanvas.height;

  const noiseData = noiseCtx.getImageData(0, 0, width, height);
  const mixMapData = new ImageData(width, height);

  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      const pixel = getPixel(noiseData, x, y, width);
      let val = pixel.reduce((prev, curr) => prev + curr, 0) / 3 / 255;

      const mixPixel = getColorByHeight2(val, levels);
      drawPixel(mixMapData, x, y, width, mixPixel);
    }
  }
  mixMapCtx.putImageData(mixMapData, 0, 0);
};

export const fillMixMapCircle = (
  mixMapCanvas: HTMLCanvasElement | null,
  noiseCanvas: HTMLCanvasElement | null,
  mapType: MapType,
  circle: {
    x: number;
    y: number;
    radius: number;
  }
) => {
  if (!noiseCanvas || !mixMapCanvas) {
    return;
  }

  const noiseCtx = noiseCanvas.getContext("2d");

  const mixMapCtx = mixMapCanvas.getContext("2d");
  if (!noiseCtx || !mixMapCtx) {
    return;
  }

  const { levels } = MapProperties[mapType];

  let { x, y, radius } = circle;

  const size = Math.floor(radius * 2);

  const noiseData = noiseCtx.getImageData(x, y, size, size);
  const mixMapData = mixMapCtx.getImageData(x, y, size, size);
  let pixel, val, mixPixel, dist;

  for (let dx = 0; dx < size; dx++) {
    for (let dy = 0; dy < size; dy++) {
      dist = Math.hypot(dx - radius, dy - radius);
      if (dist > radius) {
        continue;
      }
      pixel = getPixel(noiseData, dx, dy, size);
      val = pixel.reduce((prev, curr) => prev + curr, 0) / 3 / 255;
      mixPixel = getColorByHeight2(val, levels);
      drawPixel(mixMapData, dx, dy, size, mixPixel);
    }
  }
  mixMapCtx.putImageData(mixMapData, x, y);
};

export const fillNoiseCircle = (
  getNoiseContextAndData: GetNoiseFunctionType,
  circle: {
    x: number;
    y: number;
    radius: number;
  },
  fillMode: FillMode,
  slope: number,
  level: number
) => {
  const contextAndData = getNoiseContextAndData();
  if (!contextAndData) {
    return;
  }

  const { noiseCtx, height, width } = contextAndData;

  let { x, y, radius } = circle;

  const size = Math.floor(radius * 2);
  const noiseData = noiseCtx.getImageData(0, 0, width, height);

  let pixel, dist: number, val, sign;
  let min = 0,
    max = 255;

  for (let dx = Math.floor(x); dx < x + size; dx++) {
    for (let dy = Math.floor(y); dy < y + size; dy++) {
      dist = Math.hypot(dx - x - radius, dy - y - radius);

      if (dist > radius) {
        continue;
      }
      if (fillMode === "flat") {
        drawPixel(noiseData, dx, dy, width, [level, level, level]);
      } else {
        sign = fillMode === "add" ? 1 : -1;
        pixel = getPixel(noiseData, dx, dy, width);
        val = Math.ceil(pixel[0] + (10 - (dist / radius) * 9 * slope) * sign);

        if (val > max) {
          max = val;
        }

        if (val < 0) {
          min = val;
        }

        drawPixel(noiseData, dx, dy, width, [val, val, val]);
      }
    }
  }

  noiseCtx.putImageData(noiseData, 0, 0);

  return false;
};

function normalize(
  arr: Array<number>,
  destination: ImageData,
  width: number,
  min: number,
  max: number
) {
  //finding min and max could be avoided if a way is found to get those without iterating array
  // const min = arr.reduce(
  //   (a, b, index) => (index % 3 === 0 ? a : Math.min(a, b)),
  //   Infinity
  // );
  // const max = arr.reduce(
  //   (a, b, index) => (index % 3 === 0 ? a : Math.max(a, b)),
  //   -Infinity
  // );

  let pixel, val;
  for (let dx = 0; dx < width; dx++) {
    for (let dy = 0; dy < width; dy++) {
      pixel = getPixelFromArray(arr, dx, dy, width);
      val = Math.floor(((pixel[0] - min) / (max - min)) * 255);
      drawPixel(destination, dx, dy, width, [val, val, val]);
    }
  }
}

export const blurMixMap = (canvas: HTMLCanvasElement | null, amount = 2) => {
  if (!canvas) return;

  const url = canvas.toDataURL("image/png");
  const image = new Image();
  image.onload = () => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.filter = `blur(${amount}px)`;
    ctx.drawImage(image, 0, 0);
  };
  image.src = url;
};
